import { useRef, useEffect, useState } from "react";
import { Container, Typography, Stack, Divider, Box } from "@mui/material";
import { useReactToPrint } from "react-to-print";
import numberToWords from "number-to-words";
import { useNavigate, useLocation } from "react-router";
import dayjs from "dayjs";
import QrCodeGenerator from "views/commonComponents/QrCode/QrCodeGenerator";
import { useSelector } from "react-redux";

const A4PurchasePrint = () => {
  const componentRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const { data, tableData, qrData, typ } = location.state;
  const company = useSelector((state) => state.common.companyInfo);
  const companyLogo = company.logo ? JSON.parse(company.logo)?.img : null;
  const decimalCount = useSelector((state) => state.common.decimalCount);
  const primaryCurrency = useSelector((state) => state.common.primaryCurrency);
  const subCurrency = useSelector((state) => state.common.subCurrency);
  const isQrShow = useSelector((state) => state.common.QrShowPrint);
  const commonState = useSelector((state) => state.common);
  
  const [pages, setPages] = useState([]);
  const [calculated, setCalculated] = useState(false);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => {
      navigate(-2);
    },
  });

  useEffect(() => {
    const calculateRowHeights = () => {
      const heights = tableData.map(item => {
        const description = item.ItemNameTextField || '';
        const lineHeight = 20;
        const maxWidth = 300;
        const avgCharsPerLine = maxWidth / 8;
        const lines = Math.ceil(description.length / avgCharsPerLine);
        return Math.max(40, 30 + (lines * lineHeight));
      });
      return heights;
    };

    const paginateItems = (rowHeights) => {
      const pageHeight = 1122;
      const headerHeight = 350;
      const footerHeight = 300;
      const availableHeight = pageHeight - headerHeight - footerHeight;
      
      const newPages = [];
      let currentPage = [];
      let currentHeight = 0;

      tableData.forEach((item, index) => {
        const rowHeight = rowHeights[index];
        
        if (currentHeight + rowHeight <= availableHeight) {
          currentPage.push({ ...item, id: index + 1, rowHeight });
          currentHeight += rowHeight;
        } else {
          newPages.push(currentPage);
          currentPage = [{ ...item, id: index + 1, rowHeight }];
          currentHeight = rowHeight;
        }
      });

      if (currentPage.length > 0) {
        newPages.push(currentPage);
      }

      return newPages;
    };

    const rowHeights = calculateRowHeights();
    const paginatedData = paginateItems(rowHeights);
    setPages(paginatedData);
    setCalculated(true);
  }, [tableData]);

  useEffect(() => {
    if (company && calculated) {
      const timeoutId = setTimeout(() => {
        handlePrint();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, calculated]);

  function convertNumberToWordsWithCurrency(number) {
    const paisaAmount = (number * 100) % 100;
    const words = numberToWords.toWords(Math.floor(number));
    const paisaInWords = numberToWords.toWords(paisaAmount);
    return `${words} ${primaryCurrency} and  ${paisaInWords} ${subCurrency}`;
  }

  const amount = data.NTot;
  const result = convertNumberToWordsWithCurrency(amount);

  const generateSections = () => {
    return pages.map((page, index) => 
      renderSection(page, index + 1, pages.length)
    );
  };

  const renderSection = (sectionItems, sectionIndex, totalPages) => {
    return (
      <div
        key={`section-${sectionIndex}`}
        style={{
          marginTop: sectionIndex !== 1 ? "1rem" : "0",
          pageBreakBefore: sectionIndex !== 1 ? "always" : "auto",
          color: "black",
          borderColor: "black",
          width: "100%",
          padding: "2px",
        }}
      >
        <div style={{ border: "1px solid black" }}>
          <Stack direction="row" sx={{ height: "15%" }}>
            <div
              style={{
                width: "45%",
                borderRight: "1px solid black",
                height: "15%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-around",
                overflow: "hidden",
              }}
            >
              <div
                style={{ width: "13.3rem", height: "30%", margin: "1rem" }}
                onClick={() => {
                  navigate("/sales/orderForm");
                }}
              >
                <img
                  src={companyLogo}
                  style={{
                    objectFit: "contain",
                    height: "100%",
                    width: "100%",
                  }}
                  alt="s"
                />
              </div>

              <Container
                disableGutters
                sx={{ padding: "0.3rem", color: "black" }}
              >
                <Typography variant="h4">{company.Name}</Typography>
                <Typography>{company.Add1}</Typography>
                <Typography>{(company.city_name, company.Place)}</Typography>
                <Typography> {company.Country}</Typography>
                <Typography> Zipcode{company.Postal}</Typography>
                <Typography>Ph : {company.Ph}</Typography>
                <Typography>VAT: {company.Add2}</Typography>
              </Container>
            </div>
            <Stack direction="column" sx={{ width: "55%", height: "15%" }}>
              <Typography align="center" variant="h4" sx={{ height: "1.5rem" }}>
                {typ === "PB" && "Purchase "}
                {typ === "PI" && "Purchase Import"}
                {typ === "PO" && "Purchase Order"}
                {typ === "PR" && "Purchase Return"}
              </Typography>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  borderColor: "black",
                }}
              >
                <tbody style={{ padding: "10px", borderColor: "black" }}>
                  <tr>
                    <td
                      style={{
                        border: "1px solid black",
                        height: "1.5rem",
                      }}
                    >
                      <Typography>
                        {data.prefix
                          ? `Invoice No: ${data.prefix}`
                          : `Return No: ${data.RetNo}`}
                      </Typography>
                    </td>
                    <td
                      style={{
                        height: "1.5rem",
                        padding: 0,
                        border: "1px solid black",
                      }}
                    >
                      <div style={{ display: "flex", height: "100%" }}>
                        <Typography
                          sx={{
                            width: "80%",
                            display: "flex",
                            alignItems: "center",
                            fontSize: "12px",
                            p: 0.2,
                          }}
                        >
                          Original for Recipient
                        </Typography>
                        <div
                          style={{
                            width: "20%",
                            height: "100%",
                            borderLeft: "1px solid black",
                          }}
                        ></div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        border: "1px solid black",
                        height: "1.5rem",
                      }}
                    >
                      <Typography>
                        {typ === "OF" ? `Voucher Date:` : ` Invoice Date:`}
                        <b>{dayjs(data.BillDate).format("DD-MM-YYYY")}</b>
                      </Typography>
                    </td>
                    <td
                      style={{
                        height: "1.5rem",
                        padding: 0,
                        border: "1px solid black",
                      }}
                    >
                      <div style={{ display: "flex", height: "100%" }}>
                        <Typography
                          sx={{
                            width: "80%",
                            display: "flex",
                            alignItems: "center",
                            fontSize: "12px",
                            p: 0.2,
                          }}
                        >
                          Duplicate for Transporter
                        </Typography>
                        <div
                          style={{
                            width: "20%",
                            height: "100%",
                            borderLeft: "1px solid black",
                          }}
                        ></div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        border: "1px solid black",
                      }}
                    >
                      <Typography>
                        State Code: <b>0</b>
                      </Typography>
                    </td>
                    <td
                      style={{
                        height: "1.5rem",
                        padding: 0,
                        border: "1px solid black",
                      }}
                    >
                      <div style={{ display: "flex", height: "100%" }}>
                        <Typography
                          sx={{
                            width: "80%",
                            display: "flex",
                            alignItems: "center",
                            fontSize: "12px",
                            p: 0.2,
                          }}
                        >
                          Triplicate for Supplier
                        </Typography>
                        <div
                          style={{
                            width: "20%",
                            height: "100%",
                            borderLeft: "1px solid black",
                          }}
                        ></div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        borderBottom: "1px solid black",
                        border: "1px solid black",
                      }}
                    >
                      <Typography>
                        Client ID: <b>0</b>
                      </Typography>
                    </td>
                    <td
                      style={{
                        borderBottom: "1px solid black",
                        border: "1px solid black",
                      }}
                    >
                      <Typography>
                        Vehicle No: <b>0</b>
                      </Typography>
                    </td>
                  </tr>
                </tbody>
              </table>
              <Stack
                direction="row"
                justifyContent="space-between"
                spacing={0}
                sx={{ height: "100%", p: 1 }}
              >
                <Stack direction="column" alignItems={"start"} width={"80%"}>
                  <Typography>
                    <b style={{ display: "block" }}>To :{data.BillPartyName}</b>{" "}
                    {data.CashP}
                  </Typography>
                  <Typography>{data.warranty} </Typography>
                  <Typography>PH: {data.Ph} </Typography>
                  <Typography>
                    <b>VAT NO:</b> {data.vat_no}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="center" width={"20%"}>
                  <Box width={120}>
                    {isQrShow ? <QrCodeGenerator qrData={qrData} /> : null}
                  </Box>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          <div style={{flex:1, minHeight: "35rem" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: "left",
                      border: "1px solid black",
                      padding: "0.6rem",
                    }}
                  >
                    SI NO
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      border: "1px solid black",
                      width: "30%",
                      padding: "0.6rem",
                    }}
                  >
                    Description
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid black",
                      padding: "0.6rem",
                    }}
                  >
                    Qty
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid black",
                      padding: "0.6rem",
                    }}
                  >
                    Rate
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid black",
                      padding: "0.6rem",
                    }}
                  >
                    Unit
                  </th>
                  {typ === "PI" && (
                    <th
                      style={{
                        textAlign: "center",
                        border: "1px solid black",
                        padding: "0.6rem",
                      }}
                    >
                      Rate FC
                    </th>
                  )}
                  {typ === "PI" && (
                    <th
                      style={{
                        textAlign: "center",
                        border: "1px solid black",
                        padding: "0.6rem",
                      }}
                    >
                      Total FC
                    </th>
                  )}
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid black",
                      padding: "0.6rem",
                    }}
                  >
                    Total
                  </th>
                 { typ !== "PI" && <th
                    style={{
                      textAlign: "center",
                      border: "1px solid black",
                      padding: "0.6rem",
                    }}
                  >
                    Tax %
                  </th>}

                  { typ !== "PI" &&<th
                    style={{
                      textAlign: "right",
                      border: "1px solid black",
                      padding: "0.6rem",
                    }}
                  >
                    TaxAmnt
                  </th>}
                  <th
                    style={{
                      textAlign: "right",
                      border: "1px solid black",
                      padding: "0.6rem",
                    }}
                  >
                    NetAmount
                  </th>
                </tr>
              </thead>
              <tbody>
                {sectionItems.map((prod, index) => (
                  <tr
                    key={index}
                    style={{ height: prod.rowHeight }}
                  >
                    <td
                      style={{
                        textAlign: "center",
                        alignContent: "start",
                        borderRight: "1px solid black",
                        verticalAlign: "top",
                        paddingTop: "8px"
                      }}
                    >
                      {prod.id}
                    </td>
                    <td
                      style={{
                        textAlign: "left",
                        borderRight: "1px solid black",
                        paddingLeft: "5px",
                        wordWrap: "break-word",
                        verticalAlign: "top",
                        paddingTop: "8px"
                      }}
                    >
                      {prod.ItemNameTextField}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        borderRight: "1px solid black",
                        alignContent: "start",
                        verticalAlign: "top",
                        paddingTop: "8px"
                      }}
                    >
                      {prod.qty}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        borderRight: "1px solid black",
                        alignContent: "start",
                        verticalAlign: "top",
                        paddingTop: "8px"
                      }}
                    >
                      {parseFloat(prod.Cost).toFixed(decimalCount)}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        borderRight: "1px solid black",
                        alignContent: "start",
                        verticalAlign: "top",
                        paddingTop: "8px"
                      }}
                    >
                      {prod.Typ}
                    </td>
                    {typ === "PI" && (
                      <td
                        style={{
                          textAlign: "center",
                          borderRight: "1px solid black",
                          alignContent: "start",
                          verticalAlign: "top",
                          paddingTop: "8px"
                        }}
                      >
                        {prod.UnitRateFC}
                      </td>
                    )}
                    {typ === "PI" && (
                      <td
                        style={{
                          textAlign: "center",
                          borderRight: "1px solid black",
                          alignContent: "start",
                          verticalAlign: "top",
                          paddingTop: "8px"
                        }}
                      >
                        {prod.TotalFc}
                      </td>
                    )}
                    <td
                      style={{
                        textAlign: "center",
                        borderRight: "1px solid black",
                        alignContent: "start",
                        verticalAlign: "top",
                        paddingTop: "8px"
                      }}
                    >
                      {prod.total}
                    </td>
                   { typ !== "PI" && <td
                      style={{
                        textAlign: "center",
                        borderRight: "1px solid black",
                        alignContent: "start",
                        verticalAlign: "top",
                        paddingTop: "8px"
                      }}
                    >
                      {parseFloat(prod.Tax1).toFixed(decimalCount)}
                    </td>}

                    { typ !== "PI" &&<td
                      style={{
                        textAlign: "right",
                        borderRight: "1px solid black",
                        padding: "5px",
                        alignContent: "start",
                        verticalAlign: "top",
                        paddingTop: "8px"
                      }}
                    >
                      {parseFloat(prod.taxAmt).toFixed(decimalCount)}
                    </td>}
                    <td
                      style={{
                        textAlign: "right",
                        borderRight: "1px solid black",
                        padding: "5px",
                        alignContent: "start",
                        verticalAlign: "top",
                        paddingTop: "8px"
                      }}
                    >
                      {prod.netValue}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Divider sx={{ borderBottom: "0.5px solid black" }} />
          </div>
          <Stack direction="row" height="11rem">
            <Stack
              direction="column"
              sx={{
                height:"100%",
                width: "60%",
                borderRight: "1px solid black",
              }}
            >
              <Container
                disableGutters
                sx={{ padding: "0.3rem", borderTop: "1px solid black" }}
              >
                <Typography> Amount In Words :</Typography>
                <Typography variant="h5" sx={{ textTransform: "capitalize" }}>
                  {result}
                </Typography>
              </Container>
              <Container
                disableGutters
                sx={{ borderTop: "1px solid black", padding: "0.3rem" }}
              >
                <Typography> Bank Details:-</Typography>
                <Typography variant="h5"> Bank Name: {company.bank}</Typography>
                <Typography variant="h5">
                  {" "}
                  A/c Name: {company.beneficiary}
                </Typography>
                <Typography variant="h5">
                  {" "}
                  A/c No: {company.account_no}
                </Typography>
                <Typography variant="h5"> Branch: {company.branch}</Typography>
                <Typography variant="h5"> IFSC: {company.ifsc_code}</Typography>
              </Container>
              <Container
                disableGutters
                sx={{
                  padding: "0.8rem 0.3rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  height: "100%",
                }}
              >
                {typ === "PI" ? (
                  <Box>
                    <Typography variant="h5">
                      Sub Tot Fc : {data.SubTotFC}
                    </Typography>
                    <Typography variant="h5">
                      Net Tot Fc : {data.NetTotFC}
                    </Typography>
                  </Box>
                ) : (
                  <div
                    style={{ textOverflow: "ellipsis" }}
                    dangerouslySetInnerHTML={{
                      __html: commonState.salesBillDeclaration,
                    }}
                  />
                )}
              </Container>
            </Stack>
            <Stack direction="column" sx={{ width: "40%",height:"100%" }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  borderTop: "1px solid black",
                  height: "2rem",
                  paddingLeft: "5px",
                  paddingRight: "5px",
                }}
              >
                <Typography variant="h5">Subtotal :</Typography>
                <Typography variant="h5">
                  {" "}
                  {data.Total.toFixed(decimalCount)}
                </Typography>
              </Stack>
              { typ !== "PI" &&<Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  borderTop: "1px solid black",
                  height: "2rem",
                  paddingLeft: "5px",
                  paddingRight: "5px",
                }}
              >
                <Typography variant="h5"> Tax Amount : </Typography>
                <Typography variant="h5">
                  {typ === "PO" || typ === "PB" || typ === "PI"
                    ? parseFloat(data?.TItTaxAmt + data?.VatA).toFixed(
                        decimalCount
                      )
                    : parseFloat(data?.TItTaxAmt + data?.BillTaxField).toFixed(
                        decimalCount
                      )}
                </Typography>
              </Stack>}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  borderTop: "1px solid black",
                  height: "2rem",
                  paddingLeft: "5px",
                  paddingRight: "5px",
                }}
              >
                <Typography variant="h5"> Discount : </Typography>
                <Typography variant="h5">
                  {typ === "PR"
                    ? parseFloat(data.TDiscAmt + data.BillDiscAmtField).toFixed(
                        decimalCount
                      )
                    : parseFloat(data.DiscAmt + data.CessP).toFixed(
                        decimalCount
                      )}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  borderBlock: "1px solid black",
                  height: "2rem",
                  paddingLeft: "5px",
                  paddingRight: "5px",
                }}
              >
                <Typography variant="h5"> Net Total : </Typography>
                <Typography variant="h5">
                  {data.NTot.toFixed(decimalCount)}
                </Typography>
              </Stack>
              <Stack
                direction="column"
                justifyContent="space-between"
                sx={{ height: "5rem", paddingLeft: "5px", paddingRight: "5px" }}
              >
                <Typography
                  align="center"
                  variant="h6"
                  sx={{ marginLeft: "auto" }}
                >
                  E&OE
                </Typography>
                <Typography align="center" variant="h6">
                  For {company.Name}
                </Typography>
                <Typography align="center" variant="h6">
                  Authorised Signatory
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </div>
        <div
          style={{
            textAlign: "center",
            fontWeight: "bold",
            marginTop: "2px",
          }}
        >
          Page {sectionIndex} of {totalPages}
        </div>
      </div>
    );
  };

  const sections = generateSections();

  return (
    <div>
      <div
        ref={componentRef}
        style={{
          background: "white",
          margin: "1rem",
          height: "auto",
        }}
      >
        {sections}
      </div>
    </div>
  );
};

export default A4PurchasePrint;